/**
 * operationLibraryApi.js — thin wrappers over Api_Functions for the operation library and the
 * node ↔ operation link (story 130). One function per Sun endpoint, named after the contract row
 * (N1-N17) it maps to; see mic-user-stories/stories/130-.../contrato-sun.md §1 (N17: story 184). No business logic
 * lives here: this is the only file that spells the `operationLibrary/*` and
 * `workflow/{id}/operation*` routes.
 */
import { apiDelete, apiGet, apiPost, apiPut } from '../../generic/Api_Functions'

const withQuery = (path, params) => {
  const entries = Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
  if (entries.length === 0) return path
  return path + '?' + new URLSearchParams(Object.fromEntries(entries)).toString()
}

// ---- Library (N1-N12) --------------------------------------------------------------------------

/**
 * N1 — `POST operationLibrary/search`: RequestPagination → Page<OperationLibraryListItem>, `code` ASC
 * by default. The search term travels in `RequestPagination.genericSearch`, the standard field of the
 * repository pagination (contrato-sun.md §7: there is no `search` field of its own).
 */
export const searchOperationLibrary = (body) => apiPost('operationLibrary/search', body)

/** N2 — `GET operationLibrary/{code}` → OperationLibraryDetail (versions DESC) */
export const getOperationDetail = (code) => apiGet('operationLibrary/' + encodeURIComponent(code))

/** N3 — `GET operationLibrary/version/{nodeId}` → OperationVersionDetail { header, jobReference } */
export const getOperationVersionDetail = (nodeId) => apiGet('operationLibrary/version/' + nodeId)

/** N4 — `GET operationLibrary/version/{nodeId}/saveMode` → OperationSaveModeOptions */
export const getOperationVersionSaveMode = (nodeId) => apiGet('operationLibrary/version/' + nodeId + '/saveMode')

/** N5 — `GET operationLibrary/version/{nodeId}/usages` → OperationUsage[] */
export const getOperationVersionUsages = (nodeId) => apiGet('operationLibrary/version/' + nodeId + '/usages')

/**
 * N17 — `GET operationLibrary/{code}/usages` → OperationUsageReport { code, workflowCount, versions[] }
 * (story 184, contrato-sun.md §1-§3): the usages of EVERY live version of a code in one read, versions DESC,
 * usages already ordered by Sun. Each usage is the N5 `OperationUsage` (read `isLatestWorkflowVersion`; Sun
 * also publishes a duplicated `latestWorkflowVersion`, to be ignored). The code travels encoded like N2/N12.
 */
export const getOperationUsagesByCode = (code) => apiGet('operationLibrary/' + encodeURIComponent(code) + '/usages')

/** N6 — `GET operationLibrary/palette[?search=]` → OperationPaletteItem[] */
export const getOperationPalette = (search) => apiGet(withQuery('operationLibrary/palette', { search }))

/** N7 — `POST operationLibrary/compare` {fromNodeId, toNodeId} → OperationComparison */
export const compareOperationVersions = ({ fromNodeId, toNodeId }) =>
  apiPost('operationLibrary/compare', { fromNodeId, toNodeId })

/** N8 — `POST operationLibrary/{scopeUUID}/commit` OperationVersionSaveRequest → OperationVersionSaveResult */
export const commitOperationScope = (scopeUUID, body) => apiPost('operationLibrary/' + scopeUUID + '/commit', body)

/**
 * N9 — `DELETE operationLibrary/{scopeUUID}` → 204. Shares its route shape with N12 (contrato-sun.md
 * §7): Sun discriminates by the UUID shape of the segment.
 */
export const discardOperationScope = (scopeUUID) => apiDelete('operationLibrary/' + scopeUUID)

/** N10 — `PUT operationLibrary/version/{nodeId}/indexValue` {indexValue} → OperationVersion */
export const changeOperationVersionIndex = (nodeId, indexValue) =>
  apiPut('operationLibrary/version/' + nodeId + '/indexValue', { indexValue })

/** N11 — `DELETE operationLibrary/version/{nodeId}` → 204 · 409 {error, usages} */
export const deleteOperationVersion = (nodeId) => apiDelete('operationLibrary/version/' + nodeId)

/** N12 — `DELETE operationLibrary/{code}` → 204 · 409 {error, usagesByVersion} */
export const deleteOperation = (code) => apiDelete('operationLibrary/' + encodeURIComponent(code))

// ---- Node ↔ operation link (N13-N16) ------------------------------------------------------------

/**
 * El `NodeOperationLink` de frontera, AMPLIADO por la historia 152 (§8.3). Los nombres de campo son
 * CONTRATO (R-HANDOFF-2) y se validan contra `NodeOperationLinkDto.java` de Sun.
 *
 * Lo devuelven igual N13, el `GET workflow/{id}` y la respuesta del guardado, así que este typedef
 * es la única descripción de la forma en Jupiter.
 *
 * @typedef {Object} NodeOperationLink
 * @property {string} nodeId
 * @property {number} workflowId
 * @property {boolean} linked `true` solo si hay vínculo VIVO (`SYNCHRONIZED` o `CUSTOMIZED`).
 *   Conserva su semántica de #130 a propósito, para no mover los gatings ya construidos.
 * @property {'SYNCHRONIZED'|'CUSTOMIZED'|'UNLINKED'|null} state NUEVO en 152. `null` ⇒ nodo
 *   embebido sin procedencia. Lo que #130 llamaba "roto" hoy se distingue con este campo: editar un
 *   nodo vinculado lo deja `CUSTOMIZED`, no lo desvincula (`RN-01.14`).
 * @property {string|null} code Código de la operación vinculada — vacío si no hay vínculo vivo
 * @property {string|null} name
 * @property {number|null} versionNumber Versión vinculada
 * @property {string|null} libraryNodeId Puntero a la versión; `null` ⇔ `state === 'UNLINKED'`
 * @property {string|null} originCode NUEVO en 152: procedencia HISTÓRICA (`RN-01.18`), que
 *   sobrevive al cambio de estado y a la desvinculación
 * @property {number|null} originVersionNumber NUEVO en 152: el número de versión de esa procedencia
 * @property {number|null} latestVersionNumber
 * @property {string|null} latestNodeId
 * @property {number|null} dismissedVersionNumber
 * @property {boolean} updateAvailable `false` si `state === 'UNLINKED'`: un nodo desvinculado no
 *   recibe avisos (`RN-01.18`)
 */

/**
 * N13 — `GET workflow/{workflowId}/operationLinks` → {@link NodeOperationLink}[]
 *
 * Historia 152 §8.5: cambio ADITIVO, sin ruta nueva. La respuesta se entrega tal cual llega; quien
 * decide qué se pinta con ella es `operationLinkIntegrity.js`.
 */
export const getWorkflowOperationLinks = (workflowId) => apiGet('workflow/' + workflowId + '/operationLinks')

/** N14 — `GET workflow/{workflowId}/node/{nodeId}/operationUpdateImpact/{targetNodeId}` → OperationUpdateImpact */
export const getOperationUpdateImpact = (workflowId, nodeId, targetNodeId) =>
  apiGet('workflow/' + workflowId + '/node/' + nodeId + '/operationUpdateImpact/' + targetNodeId)

/** N15 — `POST workflow/{workflowId}/node/{nodeId}/operationLink/dismiss` {versionNumber} → NodeOperationLink */
export const dismissOperationUpdate = (workflowId, nodeId, versionNumber) =>
  apiPost('workflow/' + workflowId + '/node/' + nodeId + '/operationLink/dismiss', { versionNumber })

/** N16 — `GET workflow/{workflowId}/node/{nodeId}/operationPublishPreview?code=` → OperationPublishPreview */
export const getOperationPublishPreview = (workflowId, nodeId, code) =>
  apiGet(withQuery('workflow/' + workflowId + '/node/' + nodeId + '/operationPublishPreview', { code }))

// ---- Errors -------------------------------------------------------------------------------------

/**
 * Normalises an error thrown by Api_Functions into `{ status, error, context }`, where `error` is the
 * verbatim business literal of contrato-sun.md §4 — the i18n key itself (§8.8.6) — and `context` holds
 * the extra fields of that case (`allowedSaveModes`, `usages`, `usagesByVersion`, `references`,
 * `blockingElements`, `existingNodeId`, `currentVersionNumber`). Empty literal for the bodiless 403s.
 */
export const readOperationLibraryError = (thrown) => {
  const data = thrown && typeof thrown.data === 'object' && thrown.data !== null ? thrown.data : {}
  const { error, ...context } = data
  return {
    status: thrown && thrown.status ? thrown.status : 0,
    error: typeof error === 'string' ? error : '',
    context
  }
}
